import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import { buildVerificationSettingsPayload, readVerificationSettings } from '../model/verification-settings.js';

export default function VerificationSettingsPage() {
  const { token, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const savingRef = useRef(false);
  const canView = hasPermission('settings.view');
  const canManage = hasPermission('settings.manage');

  useEffect(() => {
    const current = ++generation.current;
    setForm(null); setSaved(null); setError(''); setErrors({}); setSuccess(false);
    savingRef.current = false; setSaving(false);
    if (canView) {
      adminApi.getVerificationSettings(token).then((response) => {
        if (generation.current !== current) return;
        const next = readVerificationSettings(response);
        setForm(next); setSaved(next);
      }).catch((failure) => {
        if (generation.current === current) setError(failure.message || 'verificationSettings.loadError');
      });
    }
    return () => { generation.current += 1; };
  }, [token, canView, reload]);

  async function save(event) {
    event.preventDefault();
    if (!canManage || !form || savingRef.current) return;
    const effectiveForm = form.allowDepositBeforeVerification ? form : {
      ...form,
      depositLimitAmount: saved.depositLimitAmount,
      depositLimitCurrency: saved.depositLimitCurrency,
      verificationDays: saved.verificationDays,
    };
    const { payload, errors: validationErrors } = buildVerificationSettingsPayload(effectiveForm);
    setErrors(validationErrors); setSuccess(false); setError('');
    if (!payload) return;
    const current = generation.current;
    savingRef.current = true; setSaving(true);
    try {
      const response = await adminApi.updateVerificationSettings(token, payload);
      if (generation.current !== current) return;
      const next = readVerificationSettings(response);
      setForm(next); setSaved(next); setSuccess(true);
    } catch (failure) {
      if (generation.current === current) {
        setError(failure.message || 'verificationSettings.saveError');
        setErrors(Object.fromEntries((failure.errors || []).filter((item) => item.parameter).map((item) => [item.parameter, item.message])));
      }
    } finally {
      if (generation.current === current) { savingRef.current = false; setSaving(false); }
    }
  }

  if (!canView) return null;
  const disabled = !canManage || saving;
  const changed = JSON.stringify(form) !== JSON.stringify(saved);
  function change(name, value) {
    if (disabled) return;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: '' })); setError(''); setSuccess(false);
  }
  function input(name, label, props = {}) {
    return <div className="col-md-4">
      <label className="form-label" htmlFor={`verification-${name}`}>{t(label)}</label>
      <input id={`verification-${name}`} name={name} className={`form-control ${errors[name] ? 'is-invalid' : ''}`} value={form[name]} disabled={disabled} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `verification-${name}-error` : undefined} onChange={(event) => change(name, event.target.value)} {...props} />
      {errors[name] && <div className="invalid-feedback" id={`verification-${name}-error`}>{t(errors[name])}</div>}
    </div>;
  }

  return <div>
    <PageHeader title={t('verificationSettings.title')} subtitle={t('verificationSettings.subtitle')} />
    <section className="card card-body">
      <p className="text-muted">{t('verificationSettings.appliesToAll')}</p>
      {error && <div role="alert" className="alert alert-danger">{t(error)}</div>}
      {success && <div role="status" className="alert alert-success">{t('verificationSettings.saved')}</div>}
      {!form && !error && <p role="status">{t('verificationSettings.loading')}</p>}
      {!form && error && <button type="button" className="btn btn-default align-self-start" onClick={() => setReload((value) => value + 1)}>{t('verificationSettings.retry')}</button>}
      {form && <form onSubmit={save} noValidate>
        {[
          ['allowLiveAccountBeforeVerification', 'verificationSettings.allowLiveAccount'],
          ['allowDepositBeforeVerification', 'verificationSettings.allowDeposit'],
        ].map(([name, label]) => <div className="form-check mb-3" key={name}>
          <input id={`verification-${name}`} name={name} type="checkbox" className="form-check-input" checked={form[name]} disabled={disabled} onChange={(event) => change(name, event.target.checked)} />
          <label className="form-check-label" htmlFor={`verification-${name}`}>{t(label)}</label>
        </div>)}
        {form.allowDepositBeforeVerification && <div className="border rounded p-3 mb-3">
          <div className="row g-3">
            {input('depositLimitAmount', 'verificationSettings.depositLimit', { type: 'text', inputMode: 'decimal' })}
            {input('depositLimitCurrency', 'verificationSettings.currency', { type: 'text', maxLength: 3, autoCapitalize: 'characters', placeholder: 'USD' })}
            {input('verificationDays', 'verificationSettings.days', { type: 'number', min: 1, max: 365, step: 1 })}
          </div>
          <p className="form-text mb-0 mt-3">{t('verificationSettings.depositHelp')}</p>
        </div>}
        <p className="mb-1">{t('verificationSettings.withdrawalHelp')}</p>
        <p className="text-muted">{t('verificationSettings.tradingHelp')}</p>
        {canManage && <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="button" className="btn btn-default" disabled={disabled || !changed} onClick={() => { setForm(saved); setErrors({}); setError(''); setSuccess(false); }}>{t('verificationSettings.reset')}</button>
          <button type="submit" className="btn btn-primary" disabled={disabled || !changed}>{t(saving ? 'verificationSettings.saving' : 'verificationSettings.save')}</button>
        </div>}
      </form>}
    </section>
  </div>;
}
